import { RootState } from "@/src/app/store"
import { DateRange } from "@/src/components/ui/daterangeselect/DateRangeSelect"
import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { AreaOfFraud, CaseStatus } from "../../../api/cases"
import { addDaysToToday, formatDateForInput, getCurrentDate } from "../../../lib/utils"
import { STATUS_OPTIONS } from "../../../components/ui/casestatusbadge/CaseStatusBadge"

const DEFAULT_DATE_RANGE_DAYS = 365;

const DEFAULT_STATUS_FILTER = STATUS_OPTIONS.filter(status => status !== CaseStatus.REJECTED);

export interface CaseListFilters {
    searchStrings: string[]
    zone: string[]
    investigator_id: string[]
    referral_date_range: DateRange
    status: CaseStatus[]
    area_of_fraud: AreaOfFraud[]
    page_size: number
    page_number: number
}

const initialState: CaseListFilters = {
    searchStrings: [],
    zone: [],
    investigator_id: [],
    referral_date_range: {
        from: formatDateForInput(addDaysToToday(-DEFAULT_DATE_RANGE_DAYS)),
        to: formatDateForInput(getCurrentDate())
    },
    status: DEFAULT_STATUS_FILTER,
    area_of_fraud: [],
    page_size: 10,
    page_number: 1
}

const caseListFiltersSlice = createSlice({
    name: 'caseListFilters',
    initialState,
    reducers: {
        setZoneFilter(
            state: CaseListFilters, 
            action: PayloadAction<string[]>
        ){
            state.zone = action.payload
            state.page_number = 1
        },
        setInvestigatorFilter(
            state: CaseListFilters, 
            action: PayloadAction<string[]>
        ){
            state.investigator_id = action.payload
            state.page_number = 1
        },
        setReferralDateRangeFilter(
            state: CaseListFilters,
            action: PayloadAction<DateRange>
        ){
            state.referral_date_range = action.payload
            state.page_number = 1
        },
        setStatusFilter(
            state: CaseListFilters,
            action: PayloadAction<CaseStatus[]>,
        ){
            state.status = action.payload
            state.page_number = 1
        },
        setAreaOfFraudFilter(
            state: CaseListFilters,
            action: PayloadAction<AreaOfFraud[]>,
        ){
            state.area_of_fraud = action.payload
            state.page_number = 1
        },
        setPageNumber(
            state: CaseListFilters, 
            action: PayloadAction<number>
        ){
            state.page_number = action.payload
        },
        setPageSize(
            state: CaseListFilters,
            action: PayloadAction<number>
        ){
            state.page_size = action.payload;
            state.page_number = 1
        },
        setSearchStrings(
            state : CaseListFilters, 
            action: PayloadAction<string[]>
        ){
            state.searchStrings = action.payload
            state.page_number = 1
        }
    }
})

export const getSearchStrings = (state: RootState) => (state.caseFilters.searchStrings)
export const getSelectedZoneValues = (state: RootState) => (state.caseFilters.zone)
export const getSelectedInvestigatorValues = (state: RootState) => (state.caseFilters.investigator_id)
export const getSelectedReferralDateRange = (state: RootState) => (state.caseFilters.referral_date_range)
export const getSelectedStatus = (state: RootState) => (state.caseFilters.status)
export const getSelectedAreaOfFraud = (state: RootState) => (state.caseFilters.area_of_fraud)
export const selectCaseFilterCount = (state: RootState) => {
    let count = 0;
    if(state.caseFilters.zone.length > 0)
        count++;
    if(state.caseFilters.investigator_id.length > 0)
        count++;
    if(state.caseFilters.status.length > 0)
        count++;
    if(state.caseFilters.area_of_fraud.length > 0)
        count++;

    return count;
}
export const getPageNumber = (state: RootState) => (state.caseFilters.page_number)
export const getPageSize = (state: RootState) => (state.caseFilters.page_size)
export const getCaseFilters = (state: RootState) => (state.caseFilters)

export const { setZoneFilter, setInvestigatorFilter, setReferralDateRangeFilter, setStatusFilter, setAreaOfFraudFilter, setPageNumber, setSearchStrings, setPageSize } = caseListFiltersSlice.actions;

export const caseFiltersReducer = caseListFiltersSlice.reducer;